import React from 'react'
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {ChevronDownIcon} from '@chakra-ui/icons'
import {useHistory} from 'react-router-dom'

import {SideMenuTemplate} from '../SideMenuTemplate/SideMenuTemplate'
import {useAuth} from '../../stores/AuthStore'
import copy from './copy.json'
import {CustomIcon, OmniSelect} from '../../components'
import {useAuthClients} from '../../queries'
import {useCanSwitchCamps} from '../../hooks'
import {EmptyResultError} from '../../customErrors'

interface Props {
  width: string[]
}

export const PrimaryNavigation: React.FC<React.PropsWithChildren<Props>> = ({
  width,
}: Props): React.ReactElement => {
  const {isOpen, onOpen, onClose} = useDisclosure()
  const {
    person,
    client,
    logout,
    setAuthClientID,
    useIdpAuthentication,
    authClientID,
    setError,
  } = useAuth()
  const history = useHistory()
  const {data: campPickerOptions} = useAuthClients({
    select: (authClients) =>
      authClients.map((c) => ({
        key: c.id,
        value: c.id,
        label: c.name,
        meta: {
          id: c.id,
          domainName: c.domainName,
        },
      })),
    onSuccess: (shapedData) => {
      // Automatically set auth client ID if there's only one option
      if (shapedData.length === 1) {
        setAuthClientID(shapedData[0].value)
      }

      if (shapedData.length === 0) {
        setAuthClientID('') // clear auth client id
        setError(new EmptyResultError('No Auth Client Results for this user'))
        history.push('/auth-error')
      }
    },
    useIdpAuthentication,
  })

  const handleLogout = async (): Promise<void> => {
    history.push('/logout')
    // logout() rejects on server-logout failure (already logged inside it).
    // This runs from an onClick handler React won't await, so swallow to
    // avoid an unhandled rejection.
    await logout().catch(() => {
      // already logged inside AuthStore.logout
    })
  }

  // Using Auth0 and the header isn't being used for an unauthenticated status
  // and react query is done fetching auth clients and there is more than one
  // camp to pick from. `useCanSwitchCamps` is the single source of truth for
  // this predicate (shared with the employee-only FR-confidence gate).
  const showCampPicker = useCanSwitchCamps()

  return (
    <Flex
      width={width}
      borderBottom={['1px']}
      borderTop={['0', '3px solid #E68C4D']}
      height={[showCampPicker ? '80px' : '40px', '64px']}
      position='fixed'
      bg='white'
      zIndex={10}>
      <Flex
        width={width}
        flexDir='column'
        display={['flex']}
        justifyContent='center'>
        <Flex width={width} display={['flex']} flexDir='row'>
          <Flex width={['3%', '20%']} justifyContent='flex-start'>
            <Button
              aria-label={copy.navigationMenu.label}
              onClick={onOpen}
              h='100%'
              border='none'
              variant='outline'>
              <CustomIcon name='bell' ml={[2]} boxSize='1.5rem' />
            </Button>
            <CustomIcon
              name='phone'
              maxWidth='8rem'
              ml={[2]}
              boxSize='100%'
              display={['none', 'inline-block']}
            />
          </Flex>
          <Flex
            width={['94%', '10%']}
            justifyContent={['center', 'flex-start']}>
            <CustomIcon
              name='star'
              boxSize='10'
              ml={[0]}
              display={['inline-block', 'none']}
            />
          </Flex>
          <Flex width={['3%', '70%']} justifyContent='flex-end' flexDir='row'>
            {showCampPicker && (
              <Box mr={3} display={['none', 'block']}>
                <OmniSelect
                  selectedValue={authClientID}
                  options={campPickerOptions || []}
                  onSelect={(value): void => {
                    setAuthClientID(value)
                  }}
                  showSearch={
                    campPickerOptions && campPickerOptions.length > 10
                  }
                />
              </Box>
            )}
            <Menu>
              <MenuButton data-testid='user-menu-button'>
                <Flex justifyContent='right' flexDir='row' alignItems='center'>
                  <Flex
                    flexDir='column'
                    display={['none', 'inline-block']}
                    mr={[3]}>
                    {person && (
                      <Text
                        fontSize='sm'
                        textAlign='left'
                        lineHeight='1rem'
                        data-testid='loggedin-user-name'>
                        {person.name.first}
                      </Text>
                    )}
                    {client &&
                      (!useIdpAuthentication ||
                        (useIdpAuthentication && !showCampPicker)) && (
                        <Text
                          fontSize='sm'
                          textAlign='left'
                          lineHeight='1rem'
                          data-testid='loggedin-client-name'>
                          {client.name}
                        </Text>
                      )}
                  </Flex>
                  <ChevronDownIcon
                    alignSelf='center'
                    display='inline-block'
                    color='current.menu.iconColor'
                    w='24px'
                    h='24px'
                    mr={3}
                  />
                </Flex>
              </MenuButton>
              <MenuList data-testid='user-menu'>
                <MenuItem
                  as='a'
                  onClick={(): void => {
                    window.open('https://campanionapp.com/support/admin-help/')
                  }}>
                  {copy.userMenu.links.help.label}
                </MenuItem>
                <MenuItem as='a' onClick={handleLogout}>
                  {copy.userMenu.links.logout.label}
                </MenuItem>
              </MenuList>
            </Menu>
            <SideMenuTemplate isOpen={isOpen} onClose={onClose} />
          </Flex>
        </Flex>
        {/* mobile FOR NOW */}
        <Flex width={width} display={['flex', 'none']} flexDir='row'>
          {showCampPicker && (
            <Box width={['100%']} justifyContent='flex-start'>
              <OmniSelect
                selectedValue={authClientID}
                options={campPickerOptions || []}
                onSelect={(value): void => {
                  setAuthClientID(value)
                }}
                showSearch={campPickerOptions && campPickerOptions.length > 10}
              />
            </Box>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
